/**
 * TypeScript types for Action Proposals
 *
 * Typed GTM actions flowing through the governed pipeline
 * (propose -> gate -> execute -> rollback). Every proposal carries an
 * autonomy tier: Tier-1 (policy auto-approvable, cheap/reversible) or
 * Tier-2 (human-gated). Platform floors mean spend-touching actions are
 * always Tier-2 - the tier is assigned server-side and is read-only here.
 *
 * Story 9.4 - Action Approval UX; generalized for GTM connectors (Track 5).
 */

export type ActionType =
  | 'pause_campaign'
  | 'resume_campaign'
  | 'reduce_budget'
  | 'increase_budget'
  | 'adjust_budget'
  | 'adjust_bid'
  | 'adjust_targeting'
  | 'modify_bidding'
  | 'update_targeting'
  | 'update_schedule'
  | 'sandbox_experiment'
  | 'add_negative_keyword';

export type ActionStatus =
  | 'proposed'
  | 'approved'
  | 'rejected'
  | 'expired'
  | 'cancelled'
  | 'executed'
  | 'failed'
  | 'rolled_back';

/**
 * Connector a typed action writes through. `sandbox` is the internal
 * test-double executor used by the belief loop; it touches nothing external.
 */
export type TargetConnector = 'meta' | 'google' | 'tiktok' | 'sandbox';

/** @deprecated Use TargetConnector - kept as an alias during generalization. */
export type TargetPlatform = TargetConnector;

export type TargetEntityType = 'campaign' | 'ad_set' | 'ad';

export type RiskLevel = 'low' | 'medium' | 'high';

/**
 * Autonomy tier of a proposal. tier1_auto = eligible for policy
 * auto-approval (cheap, reversible, non-spend); tier2_gated = requires a
 * human decision in this inbox. Spend-touching actions are floored at
 * Tier-2 by the platform and can never be relaxed by configuration.
 */
export type ProposalTier = 'tier1_auto' | 'tier2_gated';

export interface TargetInfo {
  platform: TargetConnector;
  entity_type: TargetEntityType;
  entity_id: string;
  entity_name: string | null;
}

/** Context for a belief-sourced proposal: what the test tries to learn. */
export interface SourceBeliefSummary {
  id: string;
  statement: string;
  domain: string;
  status: string;
  stored_confidence: number;
  decayed_confidence: number;
}

/** The reasoner's design context persisted on the proposal (subset). */
export interface TestDesignSummary {
  hypothesis: string | null;
  rationale: string | null;
  proxy_metric: string | null;
}

export interface ActionProposal {
  proposal_id: string;
  action_type: ActionType;
  status: ActionStatus;
  target: TargetInfo;
  proposed_change: Record<string, unknown>;
  current_value: Record<string, unknown> | null;
  expected_effect: string;
  risk_disclaimer: string;
  risk_level: RiskLevel;
  tier: ProposalTier;
  confidence_score: number;
  requires_approval: boolean;
  expires_at: string;
  created_at: string;
  decided_at: string | null;
  decided_by: string | null;
  decision_reason: string | null;
  source_recommendation_id: string | null;
  source_belief_id: string | null;
  source_belief?: SourceBeliefSummary | null;
  test_design?: TestDesignSummary | null;
  /**
   * The catalog specialist whose artifact bridged into this proposal
   * (spec #44 §6 "Gap A" / #46 §3). Stamped server-side at
   * `proposal_bridge.py`, exposed from `proposal_metadata` the same way
   * `test_design` is. WITHOUT IT a bridged proposal cannot be attributed
   * to any agent and the questions lane would have to guess — which it
   * never does: an unattributable proposal says so on its card.
   * Optional here because a belief-sourced proposal has none.
   */
  specialist_key?: string | null;
  /** The artifact the bridged finding came from (#46 §3, same exposure). */
  artifact_id?: string | null;
  /**
   * Set on a plan-approval proposal, from `proposal_metadata.plan_version`
   * (spec #45 §6). Its presence — not its value — is what gives the card
   * the plan face rather than the budget-change face.
   */
  plan_version?: number | null;
}

export interface ActionProposalsListResponse {
  proposals: ActionProposal[];
  total: number;
  has_more: boolean;
  pending_count: number;
}

export interface ProposalActionResponse {
  status: string;
  proposal_id: string;
  new_status: ActionStatus;
}

export interface ActionProposalsFilters {
  status?: ActionStatus;
  /** Accepted delta 2: the per-agent Approvals tab queries rather than
   * scanning every page for a matching `specialist_key`. */
  specialist_key?: string;
  action_type?: ActionType;
  platform?: TargetConnector;
  risk_level?: RiskLevel;
  tier?: ProposalTier;
  limit?: number;
  offset?: number;
}

export type AuditAction =
  | 'created'
  | 'approved'
  | 'rejected'
  | 'expired'
  | 'cancelled'
  | 'executed'
  | 'failed'
  | 'rolled_back';

export interface AuditEntry {
  id: string;
  action: AuditAction;
  performed_at: string;
  performed_by: string | null;
  performed_by_role: string | null;
  previous_status: ActionStatus | null;
  new_status: ActionStatus;
  reason: string | null;
}

export interface AuditTrailResponse {
  proposal_id: string;
  entries: AuditEntry[];
}

export interface PendingCountResponse {
  pending_count: number;
}

/**
 * Get display label for action type
 */
export function getActionTypeLabel(type: ActionType): string {
  const labels: Record<ActionType, string> = {
    pause_campaign: 'Pause Campaign',
    resume_campaign: 'Resume Campaign',
    reduce_budget: 'Reduce Budget',
    increase_budget: 'Increase Budget',
    adjust_budget: 'Adjust Budget',
    adjust_bid: 'Adjust Bid',
    adjust_targeting: 'Adjust Targeting',
    modify_bidding: 'Modify Bidding',
    update_targeting: 'Update Targeting',
    update_schedule: 'Update Schedule',
    sandbox_experiment: 'Sandbox Experiment',
    add_negative_keyword: 'Add Negative Keyword',
  };
  return labels[type] || type;
}

/**
 * Get display label for action status
 */
export function getStatusLabel(status: ActionStatus): string {
  const labels: Record<ActionStatus, string> = {
    proposed: 'Pending Approval',
    approved: 'Approved',
    rejected: 'Rejected',
    expired: 'Expired',
    cancelled: 'Cancelled',
    executed: 'Executed',
    failed: 'Failed',
    rolled_back: 'Rolled Back',
  };
  return labels[status] || status;
}

/**
 * Get status badge tone
 */
export function getStatusTone(status: ActionStatus): 'info' | 'success' | 'warning' | 'critical' | undefined {
  const tones: Record<ActionStatus, 'info' | 'success' | 'warning' | 'critical' | undefined> = {
    proposed: 'warning',
    approved: 'success',
    rejected: 'critical',
    expired: undefined,
    cancelled: undefined,
    executed: 'success',
    failed: 'critical',
    rolled_back: 'warning',
  };
  return tones[status];
}

/**
 * Get risk level badge tone
 */
export function getRiskTone(risk: RiskLevel): 'info' | 'warning' | 'critical' {
  const tones: Record<RiskLevel, 'info' | 'warning' | 'critical'> = {
    low: 'info',
    medium: 'warning',
    high: 'critical',
  };
  return tones[risk];
}

/**
 * Get tier badge label. Tier-2 is the human gate; Tier-1 is
 * policy-auto-approvable (cheap/reversible, non-spend).
 */
export function getTierLabel(tier: ProposalTier): string {
  const labels: Record<ProposalTier, string> = {
    tier1_auto: 'Tier 1 · Auto',
    tier2_gated: 'Tier 2 · Human approval',
  };
  return labels[tier] || tier;
}

/**
 * Get tier badge tone. Tier-2 (gated) is highlighted because it is the
 * set awaiting a human decision.
 */
export function getTierTone(tier: ProposalTier): 'info' | 'attention' {
  return tier === 'tier1_auto' ? 'info' : 'attention';
}

/**
 * Get connector display name.
 */
export function getConnectorLabel(connector: TargetConnector): string {
  const labels: Record<TargetConnector, string> = {
    meta: 'Meta Ads',
    google: 'Google Ads',
    tiktok: 'TikTok Ads',
    sandbox: 'Sandbox (internal)',
  };
  return labels[connector] || connector;
}

/** @deprecated Use getConnectorLabel - kept as an alias during generalization. */
export const getPlatformLabel = getConnectorLabel;

/**
 * Check if proposal can be approved/rejected
 */
export function canDecideProposal(proposal: ActionProposal): boolean {
  return proposal.status === 'proposed' && new Date(proposal.expires_at) > new Date();
}

/**
 * Check if proposal is expired
 */
export function isProposalExpired(proposal: ActionProposal): boolean {
  return proposal.status === 'proposed' && new Date(proposal.expires_at) <= new Date();
}
